import { mapValues } from 'lodash';
import { dataMember, required } from 'santee-dcts';

import { CategoryNaturalKey } from './../documents/models/category';

export type PermissionName =
    | 'ViewLabManuals'
    | 'ViewSiteActions'
    | 'ViewSiteCommunications'
    | 'ViewSponsorCommunications'
    | 'ViewTrainingMaterials'

    | 'UploadLabManuals'
    | 'UploadSiteActions'
    | 'UploadSiteCommunications'
    | 'UploadSponsorCommunications'
    | 'UploadTrainingMaterials'
    | 'UploadApplicationTraining'
    | 'UploadGeneralCommunications'
    | 'UploadGeneralManuals'
    | 'UploadGeneralTrainingMaterials'

    | 'AccessUserProfile'
    | 'ProtocolAccess'
    | 'ProtocolReadAccess'
    | 'ViewAuditLogs'
    | 'EmulateUser'
    | 'UploadCommonLinks'
    | 'UploadAnnouncements'
    | 'HideUnhideDocuments'

    | 'AccessInvestigationReport'
    | 'ViewActivityReportProtocolAccess'
    | 'ViewActivityReportLabManuals'
    | 'ViewActivityReportLabReports'
    | 'ViewActivityReportResultAlerts'
    | 'ViewActivityReportSiteActions'
    | 'ViewActivityReportSiteCommunications'
    | 'ViewActivityReportTrainingMaterials'

    | 'ViewKitInventory'
    | 'KitInventoryandExpirationReport'

    | 'EQueryViewAndRespond'
    | 'EQueryCreationRespondClose'
    | 'EQueryAdministration'
    | 'ViewActivityReportEQuery'
    | 'ViewStudyHealthDashboard'
    | 'ConfigureViews'
    ;

export class UserPermissions {
    private readonly permissions: Set<string>;

    constructor(permissions: string[]) {
        this.permissions = new Set(permissions);
    }

    public has(permission: PermissionName): boolean {
        return this.permissions.has(permission);
    }

    public canViewCategory(categoryId: CategoryNaturalKey): boolean {
        switch (categoryId) {
            case 'lab-manuals': return this.has('ViewLabManuals');
            case 'synopsis': return this.has('ViewLabManuals');
            case 'study-manuals': return this.has('ViewLabManuals');
            case 'lab-manuals-others': return this.has('ViewLabManuals');

            case 'training-materials': return this.has('ViewTrainingMaterials');
            case 'training-materials-e-learning': return this.has('ViewTrainingMaterials');
            case 'training-materials-others': return this.has('ViewTrainingMaterials');

            case 'lab-reports': return true;
            case 'result-alerts': return true;

            case 'site-actions': return this.has('ViewSiteActions');
            case 'site-communications': return this.has('ViewSiteCommunications');
            case 'sponsor-communications': return this.has('ViewSponsorCommunications');
            default: return false;
        }
    }

    public canUploadCategory(categoryId: CategoryNaturalKey): boolean {
        switch (categoryId) {
            case 'lab-manuals': return this.has('UploadLabManuals');
            case 'site-actions': return this.has('UploadSiteActions');
            case 'site-communications': return this.has('UploadSiteCommunications');
            case 'sponsor-communications': return this.has('UploadSponsorCommunications');
            case 'training-materials': return this.has('UploadTrainingMaterials');
            case 'application-training': return this.has('UploadApplicationTraining');
            case 'general-communications': return this.has('UploadGeneralCommunications');
            case 'general-manuals': return this.has('UploadGeneralManuals');
            case 'general-training-materials': return this.has('UploadGeneralTrainingMaterials');
            default: return false;
        }
    }

    public canViewActivityReport(categoryId: CategoryNaturalKey): boolean {
        switch (categoryId) {
            case 'lab-manuals': return this.has('ViewActivityReportLabManuals');
            case 'lab-reports': return this.has('ViewActivityReportLabReports');
            case 'result-alerts': return this.has('ViewActivityReportResultAlerts');
            case 'site-actions': return this.has('ViewActivityReportSiteActions');
            case 'site-communications': return this.has('ViewActivityReportSiteCommunications');
            case 'training-materials': return this.has('ViewActivityReportTrainingMaterials');
            case 'equery': return this.has('ViewActivityReportEQuery');
            default: return false;
        }
    }
}

export type UserStudyPermissions = { [protocolId: string]: UserPermissions };

export class UserPermissionsReadModel {
    @dataMember({
        customDeserializer: (permissions: string[]) => new UserPermissions(permissions)
    })
    @required()
    public permissions: UserPermissions;
}

export class UserStudyPermissionsReadModel {
    @dataMember({
        customDeserializer: (permissionsDict: { [protocolId: string]: string[] }) => {
            return mapValues(permissionsDict, permissions => new UserPermissions(permissions)) as UserStudyPermissions;
        }
    })
    @required()
    public studyPermissions: UserStudyPermissions;
}